import { MessageRequestResponseProtocol } from '@fullstory/channel';
import { PlaybackServiceWorkerToClientMessage } from '@fullstory/playback-service-worker';
import { PlaybackContext } from '@fullstory/renderer-shared';
import { assertExhaustive } from '@fullstory/typeutils';
import { rewriteContent } from './rewrite';

const protocol = new MessageRequestResponseProtocol<PlaybackServiceWorkerToClientMessage>();

/**
 * Handlers for playback service worker messages. This interface allows callers
 * to provide custom handling for messages that can't be handled in a generic
 * way, or that require additional context to handle.
 *
 * TODO(sethf): We should eventually transition all playback service worker
 * message handling to this model, now that we have a reliable way to send
 * messages to the renderer iframe for events that originate in inner iframes.
 * It's much simpler to have only one set of message handlers for each renderer
 * instance.
 */
export interface PlaybackServiceWorkerMessageHandlers {
  log(level: keyof Console, message: string): void;
  recordCustomEvent(name: string, properties: { [name: string]: any }): void;
}

/**
 * Attach an event listener for messages from the playback service worker to the
 * given window.
 *
 * @param ctx - The playback context for the current renderer.
 * @param wnd - The window on which we should listen for message events. If
 * null, this function throws.
 * @param handlers - Callbacks for handling specific messages. If not provided,
 * the relevant messages will be ignored.
 */
export function addPlaybackServiceWorkerListener(
  ctx: PlaybackContext,
  wnd: Window | null,
  handlers?: PlaybackServiceWorkerMessageHandlers,
): void {
  if (!wnd) {
    throw new Error(`Can't add listener to null window`);
  }
  if (!wnd.navigator.serviceWorker) {
    throw new Error(`Service worker API unavailable`);
  }

  wnd.navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    protocol.handleMessage(event, async request => {
      switch (request.action) {
        case 'log':
          if (handlers) {
            handlers.log(request.level, request.message);
          }
          return [request, { response: 'ok' }];

        case 'rewrite':
          console.debug(`Received rewrite message from service worker for ${request.requestUrl}`);
          try {
            const rewrittenContent = await rewriteContent(
              request.requestUrl,
              request.contentType,
              request.content,
            );
            return [request, { succeeded: true, rewrittenContent }];
          } catch (e) {
            return [request, { succeeded: false, reason: (e as Error).toString() }];
          }

        case 'recordCustomEvent':
          console.debug(`Received custom event message from service worker: ${request.event}`);
          if (handlers) {
            handlers.recordCustomEvent(request.event, request.properties);
          }
          return [request, { response: 'ok' }];

        default:
          return assertExhaustive(
            request,
            `Received unexpected message from service worker: ${JSON.stringify(request)}`,
          );
      }
    });
  });
}
